import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ConfigContext = createContext(null);

const FALLBACK = {
  // Pure per-dog pricing — no base_price.
  default_per_dog_price: 20,
  default_recurring_discount_pct: 0,
  default_commission_pct: 0,
  securement_fee: 0,
  // Phase C — First-Clean Fee fallbacks. Must mirror the backend defaults
  // in `backend/server.py::INITIAL_PRICING_DEFAULTS`. If these ever drift,
  // Family E in `test_pricing_config_integrity.py` catches it. Live reads
  // always come from `/api/config/pricing`; this FALLBACK only surfaces
  // during transient outages so a stale hard-coded literal cannot leak
  // through to the UI.
  first_clean_fee_cents: 4900,
  first_clean_fee_waived: true,
  provider_display_label: "PooPartner",
  credit_label: "PooBucks",
  // Admin Portal heading TEMPLATE (Option D — 2026-06-24). The backend
  // ships `"{firstName}'s controls"` as the default; AdminPortal.jsx
  // interpolates `{firstName}` from `useAuth().user.name`. Admins can
  // override the entire template (with or without the token) in
  // Platform Settings. This local fallback matches the backend default
  // so a transient `/config/pricing` failure surfaces the same per-
  // admin label, not a stale "Pat's controls".
  admin_portal_heading: "{firstName}'s controls",
  security: { us_only_ip_restriction: false },
  updated_at: null,
};

export function ConfigProvider({ children }) {
  const [pricing, setPricing] = useState(null);

  const loadPricing = async () => {
    try {
      const { data } = await api.get("/config/pricing");
      setPricing(data);
      return data;
    } catch {
      setPricing(FALLBACK);
      return FALLBACK;
    }
  };

  useEffect(() => {
    loadPricing();
  }, []);

  const value = pricing || FALLBACK;
  const providerDisplayLabel = value.provider_display_label || "PooPartner";
  const creditLabel = value.credit_label || "PooBucks";
  const security = value.security || { us_only_ip_restriction: false };
  const pluralize = (word, n) => (n === 1 ? word : `${word}s`);

  return (
    <ConfigContext.Provider
      value={{
        pricing: value,
        loadPricing,
        // Public alias — admin portal calls this after a successful PATCH so
        // every page using providerDisplayLabel / defaults updates without
        // a full reload.
        refresh: loadPricing,
        providerDisplayLabel,
        creditLabel,
        security,
        pluralize,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error("useConfig must be used inside ConfigProvider");
  return ctx;
}
